import React, { useEffect } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import { getPasswordStringValidator } from '../../../models/schemas/Generators';
import LabeledTextbox from '../LabeledTextbox';

export type PasswordFormProps = {
  enabled: boolean;
  onPasswordEntered: (value: string) => void;
};

export type CustomerAccountFormData = {
  password?: string;
};

const PasswordForm: React.FC<PasswordFormProps> = ({
  enabled,
  onPasswordEntered,
}: PasswordFormProps) => {
  const customerLoginFormSchema = yup.object<CustomerAccountFormData>().shape({
    password: getPasswordStringValidator('Password', enabled),
  });
  const {
    register,
    formState: { errors },
    clearErrors,
    trigger,
    setValue,
    control,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(customerLoginFormSchema),
    defaultValues: {
      password: '',
    },
  });

  useEffect(() => {
    if (!enabled) {
      clearErrors('password');
      setValue('password', '');
    }
  }, [enabled, clearErrors, setValue]);

  const handlePasswordInputBlur = async (
    e: React.FocusEvent<HTMLInputElement>
  ): Promise<void> => {
    e.persist();
    if (enabled) {
      const result = await trigger('password');
      if (result) {
        onPasswordEntered(e?.target?.value);
      }
    }
  };

  return (
    <>
      {(process.env.REACT_APP_ENV === 'QA' ||
        process.env.NODE_ENV === 'development') && (
        <DevTool control={control} placement="bottom-left" />
      )}
      <LabeledTextbox
        name="password"
        label="Password"
        type="password"
        autoComplete="password"
        maxLength={20}
        disabled={!enabled}
        errorLabel={errors?.password?.message}
        register={register}
        onBlur={handlePasswordInputBlur}
      />
    </>
  );
};

export default PasswordForm;
